// apps/web/src/lib/compras/conferencia.ts — Fase 13, Plano 22 (COMP-03, EST-10, EST-12, D-08, D-10, D-15..D-18).
// Estado PURO da conferência da NF de compra. A tela só SUGERE (pedido, matéria-prima, fator, local); o usuário ajusta item a
// item e NADA é gravado no servidor antes do clique em "Confirmar recebimento": `montarPayloadRecebimento` é a única saída
// para o servidor e só é chamada na confirmação (COMP-03: sem vínculo com pedido/matéria-prima antes disso).
// O servidor recalcula conversão, custo e justificativas (T-13-22A); aqui há só o espelho para resposta imediata na tela.
import type {
  ConferenciaNotaCompra,
  ItemConferencia,
  LocalizacaoLookupEstoque,
  PedidoSugerido,
  ReceberNotaCompraInput,
  ResultadoRecebimentoNota,
  SituacaoFornecedor,
} from '@pieltec/contracts';
import { normalizarUnidade } from '@pieltec/contracts';
import { lerNumeroDigitado, paraTexto } from '../vendas/numero';

export type PedidoItemSugerido = NonNullable<ItemConferencia['pedidoItemSugerido']>;

/** Matéria-prima escolhida para o item (a sugerida da conferência ou a escolhida pelo usuário). */
export interface MaterialEscolhido {
  readonly id: string;
  readonly codigo: string;
  readonly nome: string;
  readonly unidadeEstoque: string | null;
  readonly unidadeCompra: string | null;
  readonly fatorConversao: number | null;
}

export interface ItemConferenciaEstado {
  readonly notaCompraItemId: string;
  readonly numeroItem: number;
  readonly codigoProduto: string;
  readonly descricao: string;
  readonly unidadeComercial: string;
  /** Quantidade declarada na NF (qCom) — preservada, nunca renomeada para a física. */
  readonly quantidadeComercial: number;
  readonly valorProduto: number;
  readonly material: MaterialEscolhido | null;
  readonly materialOriginalId: string | null;
  readonly pedidoItem: PedidoItemSugerido | null;
  /** false = "Sem pedido" escolhido na linha expandida. */
  readonly usarPedido: boolean;
  readonly fator: number | null;
  /** Conversão esperada (qCom x fator), 4 casas; null sem fator. */
  readonly esperada: number | null;
  /** Quantidade física recebida como digitada (pt-BR). Pré-preenchida com a esperada. */
  readonly fisicaTexto: string;
  readonly localizacaoId: string;
  readonly justificativaCorrecao: string;
  readonly justificativaExcedente: string;
  readonly justificativaForaPedido: string;
  readonly custoMedioAtual: number | null;
}

export interface EstadoConferencia {
  readonly notaId: string;
  readonly versao: number;
  readonly fornecedorSituacao: SituacaoFornecedor | null;
  readonly pedidos: readonly PedidoSugerido[];
  readonly pedidosMarcados: readonly string[];
  readonly semPedido: boolean;
  readonly justificativaSemPedido: string;
  readonly itens: readonly ItemConferenciaEstado[];
}

// ---------------------------------------------------------------------------------------------------------------------
// Aritmética de exibição (espelho do servidor, que recalcula em ponto fixo)
// ---------------------------------------------------------------------------------------------------------------------

export function arredondar(valor: number, casas: number): number {
  const fator = 10 ** casas;
  // toPrecision(15) tira o ruído binário (1,005 x 100 = 100,49999999999999) antes do meio-acima.
  return Math.round(Number((valor * fator).toPrecision(15))) / fator;
}

const CASAS_QUANTIDADE = 4;
const CASAS_CUSTO = 6;

/** Equivalente na unidade do pedido: física ÷ fator snapshot do item de pedido, 4 casas. null sem fator válido. */
export function equivalentePedido(fisica: number | null, fatorSnapshot: number | null): number | null {
  if (fisica === null || fatorSnapshot === null || !(fatorSnapshot > 0)) return null;
  return arredondar(fisica / fatorSnapshot, CASAS_QUANTIDADE);
}

/** Custo da entrada = valor do item na NF ÷ quantidade física recebida, 6 casas (só exibição; o servidor calcula o real). */
export function custoDaEntrada(valorProduto: number, quantidadeEstoqueFisica: number | null): number | null {
  if (quantidadeEstoqueFisica === null || !(quantidadeEstoqueFisica > 0)) return null;
  return arredondar(valorProduto / quantidadeEstoqueFisica, CASAS_CUSTO);
}

const FORMATO_CUSTO = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: CASAS_CUSTO });

/** "R$ 5,102041" — ao menos 2 casas, até 6 (a precisão do custo unitário). */
export function formatarCustoUnitario(valor: number | null | undefined): string {
  if (valor === null || valor === undefined) return '—';
  return `R$ ${FORMATO_CUSTO.format(valor)}`;
}

/** Fator da NF para a unidade de estoque (mesma regra de `fatorDaNota` do servidor). null = sem como converter. */
export function fatorParaMaterial(unidadeComercial: string, material: MaterialEscolhido, pedidoItem: PedidoItemSugerido | null): number | null {
  if (material.unidadeEstoque === null) return null;
  const comercial = normalizarUnidade(unidadeComercial);
  if (comercial === normalizarUnidade(material.unidadeEstoque)) return 1;
  if (material.unidadeCompra !== null && normalizarUnidade(material.unidadeCompra) === comercial) return material.fatorConversao;
  return pedidoItem?.fatorPedidoParaEstoqueSnapshot ?? null;
}

function esperadaDe(quantidadeComercial: number, fator: number | null): number | null {
  return fator === null ? null : arredondar(quantidadeComercial * fator, CASAS_QUANTIDADE);
}

/** Quantidade física digitada (até 4 casas, positiva). null quando vazia, ilegível ou fora do formato. */
export function fisicaDoItem(item: Pick<ItemConferenciaEstado, 'fisicaTexto'>): number | null {
  const valor = lerNumeroDigitado(item.fisicaTexto);
  if (!Number.isFinite(valor) || valor <= 0) return null;
  return Math.abs(valor * 10 ** CASAS_QUANTIDADE - Math.round(valor * 10 ** CASAS_QUANTIDADE)) < 1e-6 ? valor : null;
}

// ---------------------------------------------------------------------------------------------------------------------
// Estado inicial (a sugestão do servidor, sem gravar nada)
// ---------------------------------------------------------------------------------------------------------------------

function itemInicial(item: ItemConferencia): ItemConferenciaEstado {
  const material: MaterialEscolhido | null = item.materialSugerido
    ? {
        id: item.materialSugerido.id,
        codigo: item.materialSugerido.codigo,
        nome: item.materialSugerido.nome,
        unidadeEstoque: item.unidadeEstoque,
        unidadeCompra: item.unidadeCompraCadastro,
        fatorConversao: null,
      }
    : null;
  return {
    notaCompraItemId: item.notaCompraItemId,
    numeroItem: item.numeroItem,
    codigoProduto: item.codigoProduto,
    descricao: item.descricao,
    unidadeComercial: item.unidadeComercial,
    quantidadeComercial: item.quantidadeComercial,
    valorProduto: item.valorProduto,
    material,
    materialOriginalId: material?.id ?? null,
    pedidoItem: item.pedidoItemSugerido,
    usarPedido: item.pedidoItemSugerido !== null,
    fator: item.fatorConversao,
    esperada: item.quantidadeEstoqueEsperada,
    // Pré-preenchida com a conversão esperada; o usuário só mexe se o físico diferir.
    fisicaTexto: item.quantidadeEstoqueEsperada === null ? '' : paraTexto(item.quantidadeEstoqueEsperada),
    localizacaoId: item.estoquePreferencial?.id ?? '',
    justificativaCorrecao: '',
    justificativaExcedente: '',
    justificativaForaPedido: '',
    custoMedioAtual: item.custoMedioAtual,
  };
}

/**
 * Estado de partida da conferência: 0 pedidos = "Esta nota não tem pedido"; 1 = pré-marcado; N = nenhum pré-marcado (D-15).
 * Quando o item não tem estoque preferencial, o local padrão é o fixo "Recebimento" (se a lista de locais já chegou).
 */
export function estadoInicial(conferencia: ConferenciaNotaCompra, locais: readonly LocalizacaoLookupEstoque[] = []): EstadoConferencia {
  const base: EstadoConferencia = {
    notaId: conferencia.nota.id,
    versao: conferencia.nota.versao,
    fornecedorSituacao: conferencia.fornecedor?.situacao ?? null,
    pedidos: conferencia.pedidosSugeridos,
    pedidosMarcados: conferencia.pedidosSugeridos.filter((pedido) => pedido.preMarcado).map((pedido) => pedido.pedidoId),
    semPedido: conferencia.pedidosSugeridos.length === 0,
    justificativaSemPedido: '',
    itens: conferencia.itens.map(itemInicial),
  };
  return aplicarLocalPadrao(base, locais);
}

/** Preenche com "Recebimento" os itens ainda sem local (a lista de locais pode chegar depois da conferência). */
export function aplicarLocalPadrao(estado: EstadoConferencia, locais: readonly LocalizacaoLookupEstoque[]): EstadoConferencia {
  const recebimento = locais.find((local) => local.fixa && normalizarNome(local.nome) === 'recebimento');
  if (!recebimento || estado.itens.every((item) => item.localizacaoId !== '')) return estado;
  return { ...estado, itens: estado.itens.map((item) => (item.localizacaoId === '' ? { ...item, localizacaoId: recebimento.id } : item)) };
}

function normalizarNome(nome: string): string {
  return nome
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();
}

// ---------------------------------------------------------------------------------------------------------------------
// Edições locais (puras, imutáveis). Nenhuma chama o servidor.
// ---------------------------------------------------------------------------------------------------------------------

export function atualizarItem(estado: EstadoConferencia, notaCompraItemId: string, parcial: Partial<ItemConferenciaEstado>): EstadoConferencia {
  return { ...estado, itens: estado.itens.map((item) => (item.notaCompraItemId === notaCompraItemId ? { ...item, ...parcial } : item)) };
}

export function definirFisica(estado: EstadoConferencia, notaCompraItemId: string, texto: string): EstadoConferencia {
  return atualizarItem(estado, notaCompraItemId, { fisicaTexto: texto });
}

/**
 * Troca a matéria-prima do item (vínculo escolhido, matéria-prima criada ou fator recém-definido). O fator e a conversão
 * esperada são refeitos e a física volta à esperada. Material diferente do sugerido deixa o item "Sem pedido": o servidor
 * recusa um item de pedido de outra matéria-prima, e o usuário pode reativar o pedido na linha expandida.
 */
export function escolherMaterial(estado: EstadoConferencia, notaCompraItemId: string, material: MaterialEscolhido): EstadoConferencia {
  const atual = estado.itens.find((item) => item.notaCompraItemId === notaCompraItemId);
  if (!atual) return estado;
  const mesmoDoPedido = material.id === atual.materialOriginalId;
  const pedidoDoItem = mesmoDoPedido ? atual.pedidoItem : null;
  const fator = fatorParaMaterial(atual.unidadeComercial, material, pedidoDoItem);
  const esperada = esperadaDe(atual.quantidadeComercial, fator);
  return atualizarItem(estado, notaCompraItemId, {
    material,
    fator,
    esperada,
    fisicaTexto: esperada === null ? '' : paraTexto(esperada),
    usarPedido: mesmoDoPedido && atual.pedidoItem !== null,
    justificativaCorrecao: '',
  });
}

export function alternarPedido(estado: EstadoConferencia, pedidoId: string, marcado: boolean): EstadoConferencia {
  const restantes = estado.pedidosMarcados.filter((id) => id !== pedidoId);
  // Marcar um pedido desfaz "Esta nota não tem pedido".
  return { ...estado, pedidosMarcados: marcado ? [...restantes, pedidoId] : restantes, semPedido: marcado ? false : estado.semPedido };
}

export function definirSemPedido(estado: EstadoConferencia, semPedido: boolean): EstadoConferencia {
  // "Esta nota não tem pedido" é exclusivo: desmarca os pedidos sugeridos.
  return { ...estado, semPedido, pedidosMarcados: semPedido ? [] : estado.pedidosMarcados };
}

export function confirmarFornecedorLocal(estado: EstadoConferencia): EstadoConferencia {
  return { ...estado, fornecedorSituacao: 'ATIVO' };
}

// ---------------------------------------------------------------------------------------------------------------------
// Pedido, excedente e alertas
// ---------------------------------------------------------------------------------------------------------------------

/** Item de pedido efetivamente atendido por este item da nota (pedido marcado + "usar pedido"); null = sem pedido. */
export function pedidoEfetivo(estado: EstadoConferencia, item: ItemConferenciaEstado): PedidoItemSugerido | null {
  if (estado.semPedido || !item.usarPedido || item.pedidoItem === null) return null;
  const numero = item.pedidoItem.pedidoNumero;
  const marcado = estado.pedidos.some((pedido) => pedido.numero === numero && estado.pedidosMarcados.includes(pedido.pedidoId));
  return marcado ? item.pedidoItem : null;
}

/** A nota, na prática, não atende pedido nenhum (escolha do usuário ou nenhum item ligado): exige a justificativa da nota. */
export function semPedidoEfetivo(estado: EstadoConferencia): boolean {
  return estado.semPedido || !estado.itens.some((item) => pedidoEfetivo(estado, item) !== null);
}

/** Há pedidos sugeridos e o usuário ainda não escolheu nenhum nem "Esta nota não tem pedido" (caso N pedidos, D-15). */
export function pedidoDaNotaPendente(estado: EstadoConferencia): boolean {
  return estado.pedidos.length > 0 && !estado.semPedido && estado.pedidosMarcados.length === 0;
}

export function foraDoPedido(estado: EstadoConferencia, item: ItemConferenciaEstado): boolean {
  return !semPedidoEfetivo(estado) && pedidoEfetivo(estado, item) === null;
}

export interface GrupoExcedente {
  readonly pedidoCompraItemId: string;
  readonly saldo: number;
  readonly equivalenteTotal: number;
  /** Quanto passa do saldo (> 0). */
  readonly excedente: number;
  readonly unidadePedido: string;
  /** Item da nota (menor número) que carrega a justificativa do grupo. */
  readonly itemDaJustificativa: string;
}

function equivalenteDoItem(item: ItemConferenciaEstado, pedido: PedidoItemSugerido): number | null {
  const fisica = fisicaDoItem(item);
  if (fisica === null) return null;
  const fatorSnapshot = pedido.fatorPedidoParaEstoqueSnapshot;
  if (fatorSnapshot !== null) return equivalentePedido(fisica, fatorSnapshot);
  // Pedido na mesma unidade do estoque: o equivalente é a própria física.
  const unidadeEstoque = item.material?.unidadeEstoque ?? null;
  return unidadeEstoque !== null && normalizarUnidade(unidadeEstoque) === normalizarUnidade(pedido.unidadePedido) ? fisica : null;
}

/** Excedente agregado por item de pedido (soma de todas as linhas da NF que o atendem). Só existe com saldo positivo. */
export function excedentesDaNota(estado: EstadoConferencia): ReadonlyMap<string, GrupoExcedente> {
  const grupos = new Map<string, { pedido: PedidoItemSugerido; total: number; primeiro: ItemConferenciaEstado }>();
  for (const item of estado.itens) {
    const pedido = pedidoEfetivo(estado, item);
    if (pedido === null) continue;
    const equivalente = equivalenteDoItem(item, pedido);
    if (equivalente === null) continue;
    const grupo = grupos.get(pedido.pedidoCompraItemId);
    if (grupo) {
      grupo.total = arredondar(grupo.total + equivalente, CASAS_QUANTIDADE);
      if (item.numeroItem < grupo.primeiro.numeroItem) grupo.primeiro = item;
    } else {
      grupos.set(pedido.pedidoCompraItemId, { pedido, total: equivalente, primeiro: item });
    }
  }
  const excedentes = new Map<string, GrupoExcedente>();
  for (const [id, grupo] of grupos) {
    const saldo = grupo.pedido.saldoAReceber;
    if (!(saldo > 0)) continue;
    const excedente = arredondar(grupo.total - saldo, CASAS_QUANTIDADE);
    if (excedente > 0) {
      excedentes.set(id, {
        pedidoCompraItemId: id,
        saldo,
        equivalenteTotal: grupo.total,
        excedente,
        unidadePedido: grupo.pedido.unidadePedido,
        itemDaJustificativa: grupo.primeiro.notaCompraItemId,
      });
    }
  }
  return excedentes;
}

export function excedenteDoItem(estado: EstadoConferencia, item: ItemConferenciaEstado): GrupoExcedente | null {
  const pedido = pedidoEfetivo(estado, item);
  return pedido === null ? null : (excedentesDaNota(estado).get(pedido.pedidoCompraItemId) ?? null);
}

/** Física diferente da conversão esperada (D-10): chip "Quantidade corrigida" + justificativa obrigatória. */
export function quantidadeCorrigida(item: ItemConferenciaEstado): boolean {
  const fisica = fisicaDoItem(item);
  return fisica !== null && item.esperada !== null && arredondar(fisica, CASAS_QUANTIDADE) !== arredondar(item.esperada, CASAS_QUANTIDADE);
}

export type BloqueioItem = 'SEM_VINCULO' | 'SEM_FATOR' | 'QUANTIDADE_INVALIDA' | 'SEM_LOCAL';

/** O que impede o item de dar entrada (rose). A nota inteira só é confirmada com todos os itens resolvidos. */
export function bloqueiosDoItem(item: ItemConferenciaEstado): readonly BloqueioItem[] {
  const bloqueios: BloqueioItem[] = [];
  if (item.material === null) {
    bloqueios.push('SEM_VINCULO');
    return bloqueios;
  }
  if (item.fator === null) {
    bloqueios.push('SEM_FATOR');
    return bloqueios;
  }
  if (fisicaDoItem(item) === null) bloqueios.push('QUANTIDADE_INVALIDA');
  if (item.localizacaoId === '') bloqueios.push('SEM_LOCAL');
  return bloqueios;
}

/** Unidade da NF diverge do cadastro (aviso amber, não bloqueia): fator veio do pedido, não do cadastro. */
export function unidadeDivergente(item: ItemConferenciaEstado): boolean {
  if (item.material === null || item.material.unidadeEstoque === null || item.fator === null) return false;
  const comercial = normalizarUnidade(item.unidadeComercial);
  if (comercial === normalizarUnidade(item.material.unidadeEstoque)) return false;
  return item.material.unidadeCompra === null || normalizarUnidade(item.material.unidadeCompra) !== comercial;
}

export type CodigoAlertaLinha = 'SEM_VINCULO' | 'SEM_FATOR' | 'UNIDADE_DIVERGENTE' | 'FORA_PEDIDO' | 'EXCEDENTE' | 'QUANTIDADE_CORRIGIDA';

/** Alertas visíveis do item, na ordem de gravidade: rose (bloqueia a entrada) primeiro, depois amber (atenção, exige justificativa). */
export function alertasDoItem(estado: EstadoConferencia, item: ItemConferenciaEstado): readonly CodigoAlertaLinha[] {
  const alertas: CodigoAlertaLinha[] = [];
  if (item.material === null) alertas.push('SEM_VINCULO');
  else if (item.fator === null) alertas.push('SEM_FATOR');
  if (unidadeDivergente(item)) alertas.push('UNIDADE_DIVERGENTE');
  if (foraDoPedido(estado, item)) alertas.push('FORA_PEDIDO');
  if (excedenteDoItem(estado, item) !== null) alertas.push('EXCEDENTE');
  if (quantidadeCorrigida(item)) alertas.push('QUANTIDADE_CORRIGIDA');
  return alertas;
}

export type CampoJustificativa = 'correcao' | 'excedente' | 'foraPedido' | 'semPedido';

export interface JustificativaPendente {
  /** null = justificativa da nota (sem pedido). */
  readonly notaCompraItemId: string | null;
  readonly campo: CampoJustificativa;
}

function vazio(texto: string): boolean {
  return texto.trim() === '';
}

/** Justificativas obrigatórias ainda em branco. Compara a FÍSICA × esperada (D-10), o excedente agregado (D-16) e o fora de pedido. */
export function justificativasPendentes(estado: EstadoConferencia): readonly JustificativaPendente[] {
  const pendentes: JustificativaPendente[] = [];
  const excedentes = excedentesDaNota(estado);
  const excedentesJustificados = new Set([...excedentes.values()].map((grupo) => grupo.itemDaJustificativa));
  for (const item of estado.itens) {
    if (quantidadeCorrigida(item) && vazio(item.justificativaCorrecao)) pendentes.push({ notaCompraItemId: item.notaCompraItemId, campo: 'correcao' });
    if (excedentesJustificados.has(item.notaCompraItemId) && vazio(item.justificativaExcedente)) {
      pendentes.push({ notaCompraItemId: item.notaCompraItemId, campo: 'excedente' });
    }
    if (foraDoPedido(estado, item) && vazio(item.justificativaForaPedido)) pendentes.push({ notaCompraItemId: item.notaCompraItemId, campo: 'foraPedido' });
  }
  if (!pedidoDaNotaPendente(estado) && semPedidoEfetivo(estado) && vazio(estado.justificativaSemPedido)) {
    pendentes.push({ notaCompraItemId: null, campo: 'semPedido' });
  }
  return pendentes;
}

// ---------------------------------------------------------------------------------------------------------------------
// Rodapé, confirmação e payload
// ---------------------------------------------------------------------------------------------------------------------

export function fornecedorPendente(estado: EstadoConferencia): boolean {
  return estado.fornecedorSituacao !== 'ATIVO';
}

export interface ResumoRodape {
  readonly quantidadeItens: number;
  /** Soma do valor dos itens da NF (vProd). */
  readonly total: number;
  readonly itensBloqueados: number;
  readonly justificativasPendentes: number;
  readonly pedidoPendente: boolean;
  readonly fornecedorPendente: boolean;
}

export function resumoRodape(estado: EstadoConferencia): ResumoRodape {
  return {
    quantidadeItens: estado.itens.length,
    total: arredondar(
      estado.itens.reduce((soma, item) => soma + item.valorProduto, 0),
      2,
    ),
    itensBloqueados: estado.itens.filter((item) => bloqueiosDoItem(item).length > 0).length,
    justificativasPendentes: justificativasPendentes(estado).length,
    pedidoPendente: pedidoDaNotaPendente(estado),
    fornecedorPendente: fornecedorPendente(estado),
  };
}

/** "Confirmar recebimento" só habilita sem bloqueio, sem justificativa pendente e com fornecedor confirmado. */
export function podeConfirmar(estado: EstadoConferencia): boolean {
  const resumo = resumoRodape(estado);
  return resumo.quantidadeItens > 0 && resumo.itensBloqueados === 0 && resumo.justificativasPendentes === 0 && !resumo.pedidoPendente && !resumo.fornecedorPendente;
}

/** Texto do rodapé quando o botão está desabilitado (nunca só a cor do botão). */
export function motivoDoBloqueio(estado: EstadoConferencia): string | null {
  const resumo = resumoRodape(estado);
  const motivos: string[] = [];
  if (resumo.fornecedorPendente) motivos.push('Confirme o fornecedor');
  if (resumo.pedidoPendente) motivos.push('Escolha o pedido ou "Esta nota não tem pedido"');
  if (resumo.itensBloqueados > 0) motivos.push(`${resumo.itensBloqueados} ${resumo.itensBloqueados === 1 ? 'item bloqueado' : 'itens bloqueados'}`);
  if (resumo.justificativasPendentes > 0) {
    motivos.push(`${resumo.justificativasPendentes} ${resumo.justificativasPendentes === 1 ? 'justificativa pendente' : 'justificativas pendentes'}`);
  }
  return motivos.length === 0 ? null : motivos.join(' · ');
}

function aparada(texto: string): string | null {
  const limpo = texto.trim();
  return limpo === '' ? null : limpo;
}

/**
 * Corpo do `POST /compras/notas/{id}/receber`. Envia a quantidade FÍSICA (nunca a declarada renomeada) e as justificativas
 * só onde valem. `pedidosConfirmados` é só a intenção da tela: o servidor deriva e valida os pedidos pelos itens.
 * Só chame na confirmação explícita.
 */
export function montarPayloadRecebimento(estado: EstadoConferencia): ReceberNotaCompraInput {
  const excedentes = excedentesDaNota(estado);
  const justificados = new Set([...excedentes.values()].map((grupo) => grupo.itemDaJustificativa));
  const semPedido = semPedidoEfetivo(estado);
  return {
    versao: estado.versao,
    pedidosConfirmados: semPedido ? [] : estado.pedidosMarcados,
    semPedido,
    justificativaSemPedido: semPedido ? aparada(estado.justificativaSemPedido) : null,
    itens: estado.itens.map((item) => {
      const pedido = pedidoEfetivo(estado, item);
      return {
        notaCompraItemId: item.notaCompraItemId,
        materialId: item.material?.id ?? '',
        pedidoCompraItemId: pedido?.pedidoCompraItemId ?? null,
        quantidadeEstoqueFisica: arredondar(fisicaDoItem(item) ?? 0, CASAS_QUANTIDADE),
        localizacaoId: item.localizacaoId,
        justificativaCorrecao: quantidadeCorrigida(item) ? aparada(item.justificativaCorrecao) : null,
        justificativaExcedente: justificados.has(item.notaCompraItemId) ? aparada(item.justificativaExcedente) : null,
        justificativaForaPedido: foraDoPedido(estado, item) ? aparada(item.justificativaForaPedido) : null,
      };
    }),
  };
}

// ---------------------------------------------------------------------------------------------------------------------
// Resposta do servidor
// ---------------------------------------------------------------------------------------------------------------------

/** Rede caída ou 5xx: o servidor pode ter gravado, então o texto nunca afirma que nada foi feito (WR-C02). */
export const MENSAGEM_FALHA_RECEBIMENTO = 'Não foi possível confirmar o recebimento. Confira na nota se ele foi registrado antes de tentar de novo.';
export const MENSAGEM_CONFLITO_NOTA = 'Esta nota foi alterada por outro usuário. Recarregue para ver as mudanças antes de salvar.';
export const MENSAGEM_SEM_PERMISSAO_RECEBER = 'Você não tem permissão para receber notas de compra.';

export interface ErroRecebimento {
  readonly mensagem: string;
  /** conflito = oferecer "Recarregar nota"; item = posicionar no item; geral = só o aviso do diálogo. */
  readonly tipo: 'conflito' | 'item' | 'geral';
  readonly notaCompraItemId: string | null;
  readonly campo: string | null;
  /**
   * Verdadeiro só quando o servidor recusou ANTES de gravar (validação 4xx, permissão): aí vale dizer "Nenhuma entrada foi feita".
   * Conflito, rede e 5xx deixam o resultado incerto e a tela prioriza "Recarregar nota".
   */
  readonly semEntrada: boolean;
}

function registro(corpo: unknown): Record<string, unknown> {
  return corpo && typeof corpo === 'object' ? (corpo as Record<string, unknown>) : {};
}

function textoOuNulo(valor: unknown): string | null {
  if (typeof valor === 'string' && valor.trim() !== '') return valor;
  if (Array.isArray(valor)) {
    const primeira = valor.find((parte): parte is string => typeof parte === 'string' && parte.trim() !== '');
    return primeira ?? null;
  }
  return null;
}

/** Traduz um erro do recebimento (status + corpo já lido) e o posiciona no item/campo quando o servidor o identifica. */
export function mensagemErroRecebimento(
  status: number,
  corpo: unknown,
  itens: ReadonlyArray<Pick<ItemConferenciaEstado, 'notaCompraItemId' | 'numeroItem'>> = [],
): ErroRecebimento {
  const dados = registro(corpo);
  const codigo = textoOuNulo(dados.codigo);
  const mensagemServidor = textoOuNulo(dados.message);
  const geral = (mensagem: string, semEntrada = false): ErroRecebimento => ({ mensagem, tipo: 'geral', notaCompraItemId: null, campo: null, semEntrada });

  if (status === 403) return geral(MENSAGEM_SEM_PERMISSAO_RECEBER, true);
  if (codigo === 'NOTA_VERSAO_DESATUALIZADA') return { mensagem: MENSAGEM_CONFLITO_NOTA, tipo: 'conflito', notaCompraItemId: null, campo: null, semEntrada: false };
  if (codigo === 'NOTA_JA_RECEBIDA') {
    return { mensagem: 'Esta nota já foi recebida. Recarregue a nota para ver o recebimento.', tipo: 'conflito', notaCompraItemId: null, campo: null, semEntrada: false };
  }
  if (codigo === 'FORNECEDOR_A_CONFIRMAR') return geral('Confirme o fornecedor desta nota antes de receber.', true);
  if (codigo === 'FORNECEDOR_INATIVO') return geral('O fornecedor desta nota está inativo.', true);
  if (status === 422 && mensagemServidor !== null) {
    const idDireto = textoOuNulo(dados.notaCompraItemId);
    const numero = /Item\s+(\d+)\b/.exec(mensagemServidor);
    const porNumero = numero ? itens.find((item) => item.numeroItem === Number(numero[1])) : undefined;
    const notaCompraItemId = idDireto ?? porNumero?.notaCompraItemId ?? null;
    return {
      mensagem: mensagemServidor,
      tipo: notaCompraItemId === null ? 'geral' : 'item',
      notaCompraItemId,
      campo: textoOuNulo(dados.campo),
      semEntrada: true,
    };
  }
  return geral(MENSAGEM_FALHA_RECEBIMENTO);
}

export interface LinhaSucessoRecebimento {
  readonly notaCompraItemId: string;
  readonly texto: string;
}

/**
 * Uma linha por item: "{código}: +{qtd} {un} em {local} · custo médio agora R$ {custo}/{un}". O custo médio vem da
 * conferência recarregada (null = a recarga falhou: a linha sai sem esse trecho em vez de inventar um valor).
 */
export function linhasDeSucesso(
  resultado: ResultadoRecebimentoNota,
  estado: EstadoConferencia,
  custoMedioPorItem: ReadonlyMap<string, number | null>,
  nomeDoLocal: (localizacaoId: string) => string,
  formatarQuantidade: (valor: number) => string,
): readonly LinhaSucessoRecebimento[] {
  return resultado.itens.map((recebido) => {
    const item = estado.itens.find((candidato) => candidato.notaCompraItemId === recebido.notaCompraItemId);
    const codigo = item?.material?.codigo ?? item?.codigoProduto ?? recebido.notaCompraItemId;
    const unidade = recebido.unidadeEstoqueSnapshot;
    const local = nomeDoLocal(recebido.localizacaoId);
    const custoMedio = custoMedioPorItem.get(recebido.notaCompraItemId) ?? null;
    const base = `${codigo}: +${formatarQuantidade(recebido.quantidadeEstoqueFisica)} ${unidade}${local === '' ? '' : ` em ${local}`}`;
    return {
      notaCompraItemId: recebido.notaCompraItemId,
      texto: custoMedio === null ? base : `${base} · custo médio agora ${formatarCustoUnitario(custoMedio)}/${unidade}`,
    };
  });
}

export function textoConfirmacao(quantidadeItens: number): string {
  return `Serão dadas entradas de ${quantidadeItens} ${quantidadeItens === 1 ? 'item' : 'itens'} no estoque com o custo da nota, e o custo médio de cada matéria-prima será atualizado.`;
}

/** Chave de acesso: 8 primeiros e 4 últimos dígitos (a completa vai para "Copiar chave de acesso"). */
export { encurtarChaveNfe as chaveAbreviada } from '../vendas/chave-nfe';
